// Phase B.2 Task 5.7 — overlay state machine.
//
// Pure reducer `nextPhase(current, inputs)` drives the PlayerLoadingOverlay.
// The overlay is rendered above the SCORM launcher iframe; the iframe stays
// `visibility: hidden` until `phase === 'done'`. The state machine is the
// single source of truth for "can we reveal the iframe yet?".
//
// Inputs are derived from parent PublicPlayer (GQL loading, session create
// mutation) + launcher postMessage bridge (Task 5.6 — `session_status`,
// `course_ready`). Timers are tracked via `elapsed*` fields populated by the
// hook using `performance.now()`.
//
// Timeouts are ceilings — every non-terminal phase has a failsafe path to
// `done` so the overlay can never block the user longer than HARD_TIMEOUT_MS.

export type OverlayPhase =
  | 'idle'
  | 'query'
  | 'session'
  | 'iframeLoad'
  | 'sessionInit'
  | 'courseReady'
  | 'done'
  | 'error'

// Timeout constants — all ms. Imported by overlay JSX for user-visible timers
// (SOFT_MESSAGE_MS, RELOAD_BUTTON_MS, FADE_OUT_MS) and by the hook for
// phase-transition timeouts. No magic numbers in components.
export const IFRAME_LOAD_TIMEOUT_MS = 5000
export const SESSION_INIT_FALLBACK_MS = 2000
export const COURSE_READY_FALLBACK_MS = 1500
export const HARD_TIMEOUT_MS = 10000
export const SOFT_MESSAGE_MS = 3000
export const RELOAD_BUTTON_MS = 6000
export const FADE_OUT_MS = 200

export interface IOverlayInputs {
  queryLoading: boolean
  sessionCreating: boolean
  sessionReady: boolean
  launcherSessionStatus: 'initialized' | 'terminated' | null
  launcherCourseReady: boolean
  iframeLoaded: boolean
  iframeErrored: boolean
  // Milliseconds since the phase that owns the timer was entered. -1 when
  // the phase hasn't been entered yet.
  elapsedInIframeLoad: number
  elapsedInSessionInit: number
  elapsedInCourseReady: number
  // Milliseconds since the overlay was mounted. Drives HARD_TIMEOUT_MS.
  elapsedTotal: number
}

// Pure reducer. Given the current phase and current inputs, return the phase
// the state machine should be in. Caller is responsible for re-invoking this
// whenever any input changes (React effect + timer ticks).
export const nextPhase = (current: OverlayPhase, inputs: IOverlayInputs): OverlayPhase => {
  // Absolute ceiling — any phase collapses to `done` after HARD_TIMEOUT_MS.
  // Checked first so a stuck state can never pin the overlay.
  if (current !== 'done' && current !== 'error' && inputs.elapsedTotal >= HARD_TIMEOUT_MS) {
    return 'done'
  }

  // iframe error is terminal from any pre-terminal phase. Parent should route
  // to error UI with reload + back-to-portal buttons.
  if (current !== 'done' && current !== 'error' && inputs.iframeErrored) {
    return 'error'
  }

  switch (current) {
    case 'idle':
      // Mount → immediately enter query phase. Overlay is always visible at
      // mount; we don't stay in `idle` past the first tick.
      return 'query'

    case 'query':
      // GraphQL course load finished → move to session creation wait.
      if (!inputs.queryLoading) {
        return 'session'
      }
      return 'query'

    case 'session':
      // Portal session created AND stored in state → iframe can mount.
      if (!inputs.sessionCreating && inputs.sessionReady) {
        return 'iframeLoad'
      }
      return 'session'

    case 'iframeLoad':
      // SCORM launcher reported iframe `onLoad` → wait for LMS init.
      if (inputs.iframeLoaded) {
        return 'sessionInit'
      }
      // iframe network/script failure → error state.
      if (inputs.elapsedInIframeLoad >= IFRAME_LOAD_TIMEOUT_MS) {
        return 'error'
      }
      return 'iframeLoad'

    case 'sessionInit':
      // Launcher emitted `session_status=initialized` (Task 5.6) → wait for
      // course_ready.
      if (inputs.launcherSessionStatus === 'initialized') {
        return 'courseReady'
      }
      // Failsafe — launcher may never emit session_status on old builds.
      if (inputs.elapsedInSessionInit >= SESSION_INIT_FALLBACK_MS) {
        return 'done'
      }
      return 'sessionInit'

    case 'courseReady':
      // Launcher emitted `course_ready` → reveal iframe.
      if (inputs.launcherCourseReady) {
        return 'done'
      }
      // Failsafe — same rationale as sessionInit.
      if (inputs.elapsedInCourseReady >= COURSE_READY_FALLBACK_MS) {
        return 'done'
      }
      return 'courseReady'

    case 'done':
    case 'error':
      return current
  }
}

// Helper — phases that own a timer need to track when they were entered so
// `elapsedIn*` can be computed. Returns the set of phases that have their own
// timer budget.
export const PHASES_WITH_TIMER: readonly OverlayPhase[] = [
  'iframeLoad',
  'sessionInit',
  'courseReady',
]
